// @flow
import * as React from 'react'
import * as topojson from 'topojson-client'
import './styles.scss'
import config from 'config'
import ChoroplethMap from 'components/choroplethMap/'
import { SVGContainer } from 'components/svgContainer'
import ActiveMunicipalityPath from 'components/activeMunicipality'
import { geoIdentity, geoPath } from 'd3-geo'
import { nest } from 'd3-collection'
import hash from 'object-hash'
import EventListenerMap from 'components/eventListenerMap'

// region Constants
const seen = config.seen
const kantone = config.kantone
// endregion

// region Type Definitions
export type TaxMapProps = {
  containerHeightRatio: number,
  filter?: {
    property: string,
    id: number
  },
  mouseOverHandler: (number, MouseEvent) => void,
  patternScale: number
}

export type GeojsonFeature = {
  type: string,
  properties: { [id: string]: number | string },
  geometry: { type: string, coordinates: Array<[number, number]> }
}

// endregion

/**
 * The main component. Draws a choropleth map of Swiss municipalities given a
 * data source.
 *
 * @param {object} props
 * @param props.mouseOverHandler
 */
export function TaxMap (props: TaxMapProps): React.Node {
  const {
    mouseOverHandler,
    filter,
    containerHeightRatio = 0.6,
    patternScale = 1
  } = props
  const mapId = `map-${hash(props, {
    excludeKeys: key => {
      return key === 'mouseOverHandler'
    }
  }).substr(0, 5)}`

  let geometryCollection = config.gemeindegrenzen

  // If a filter is defined, we nest the list using the property and then
  // only pick the features we're interested in.
  if (filter && filter.property !== '') {
    let property = filter.property
    let id = filter.id
    let nestedFeatures = nest()
      .key((d: GeojsonFeature) => d.properties[property])
      .map(config.gemeindegrenzen.geometries)

    // replace the original list with the filtered list
    let filteredList = nestedFeatures.get(id)

    geometryCollection = {
      type: 'GeometryCollection',
      geometries: filteredList
    }
  }

  /**
   * Path generator function – needs to be defined in this part, since the
   * fitSize function depends on the applied filter for easy «zooming»
   */
  let pathGenerator: GeojsonFeature => string = geoPath().projection(
    geoIdentity().fitSize(
      [1000, 1000 * containerHeightRatio],
      topojson.feature(config.schweiz, geometryCollection)
    )
  )

  const seenPfade: Element<any> = topojson
    .feature(config.schweiz, seen)
    .features.map((see, index) => {
      return (
        <path
          key={index}
          d={pathGenerator(see)}
          fill='#b8d5e6'
          className='lake'
          stroke='#a1b2bd'
          strokeWidth={1}
          vectorEffect='non-scaling-stroke'
        />
      )
    })

  const kantonsMesh = (
    <path
      d={pathGenerator(
        topojson.mesh(config.schweiz, kantone, (a, b) => a !== b)
      )}
      stroke='#111'
      strokeLinejoin='round'
      fill='transparent'
      opacity={0.33}
      strokeWidth={2}
      vectorEffect='non-scaling-stroke'
    />
  )

  const mesh = (
    <path
      d={pathGenerator(topojson.mesh(config.schweiz, geometryCollection))}
      stroke='#111'
      strokeLinejoin='round'
      fill='transparent'
      strokeOpacity={0.33}
      strokeWidth={1}
      vectorEffect='non-scaling-stroke'
    />
  )

  // Feature Detection for Internet Explorer and Edge, as they don't support
  // Drop Shadow Filters.
  const supportsDropShadowFilter =
    typeof window.SVGFEDropShadowElement !== 'undefined'

  return (
    <div
      className='swiss map'
      id={mapId}
      style={{
        // This is not really optimal, but ...maybe better?
        paddingBottom: `${80}vmin`
      }}
      onMouseOver={event => mouseOverHandler(-1, event)}
    >
      <SVGContainer
        className='choropleth non-interactive'
        width={1000}
        height={containerHeightRatio * 1000}
      >
        <defs>
          {supportsDropShadowFilter && (
            <filter id='shadow'>
              <feDropShadow dx='0 ' dy='0' stdDeviation='1' />
            </filter>
          )}
          <pattern
            id={`${mapId}-diagonalHatch`}
            width={2 * patternScale}
            height={2 * patternScale}
            patternTransform='rotate(45 0 0)'
            patternUnits='userSpaceOnUse'
          >
            <line
              x1='0'
              y1='0'
              x2='0'
              y2={4}
              vectorEffect='non-scaling-stroke'
              stroke='white'
              strokeOpacity='0.7'
              strokeWidth={1}
            />
          </pattern>
        </defs>
        <ChoroplethMap
          pathGenerator={pathGenerator}
          geometryCollection={geometryCollection}
          mapId={mapId}
        />
      </SVGContainer>

      <SVGContainer
        className='static'
        width={1000}
        height={containerHeightRatio * 1000}
      >
        <g className={'gemeinden non-interactive'}>{mesh}</g>
        <g className={'kantone non-interactive'}>{kantonsMesh}</g>
        <g className={'seen non-interactive'}>{seenPfade}</g>
        <g className={'event-listener'}>
          <EventListenerMap
            pathGenerator={pathGenerator}
            geometryCollection={geometryCollection}
            mapId={mapId}
          />
        </g>
      </SVGContainer>

      {
        // Active Municipality has to be in its own container since it is
        // being redrawn all the time
      }
      <SVGContainer
        className='active non-interactive'
        width={1000}
        height={containerHeightRatio * 1000}
      >
        <ActiveMunicipalityPath
          pathGenerator={pathGenerator}
          geometryCollection={geometryCollection}
          mapId={mapId}
        />
      </SVGContainer>
    </div>
  )
}

export default TaxMap



// WEBPACK FOOTER //
// ./src/components/map/component.js